'use client'

import { DUR, EASE } from '@/animations/settings/vars'
import { useGSAPContext, useSanityFilterSync } from '@/hooks'
import { useListScrollRestore } from '@/hooks/use-list-scroll-restore'
import { cn } from '@/lib/utils'
import {
  Button,
  CardBlog,
  CardPress,
  Container,
  Section,
  SectionHeaderContent,
  SpotSanityError,
  SpotSanityFilters
} from '@/ui/components'
import {
  getGroupedFacets,
  getMatchedFacets,
  getTitleTag,
  sorting
} from '@/utils'
import { ScrollTrigger, gsap } from '@gsap'
import { memo, useEffect, useRef, useState } from 'react'

import { useStoreI18n, useStorePressroom, useStoreStories } from '@/store'
import {
  Typo3ButtonNew,
  Typo3Image,
  Typo3SectionSpotSanityPressroom,
  Typo3SectionSpotSanityPressroomList_Item,
  Typo3SectionSpotSanityStory,
  Typo3SectionSpotSanityStoryList_Item
} from '@/types/typo3'

import { toDesignButton } from '@/lib/transformers'

const convertToDesignProps = (
  content:
    | Typo3SectionSpotSanityStory['content']
    | Typo3SectionSpotSanityPressroom['content']
) => ({
  ...content,
  buttons: content?.buttons?.map((btn: Typo3ButtonNew) => toDesignButton(btn))
})

interface Props {
  data: Typo3SectionSpotSanityStory | Typo3SectionSpotSanityPressroom
  storeType?: 'story' | 'pressroom'
}

const List = ({ data, storeType = 'story' }: Props) => {
  const D = useStoreI18n.use.dictionary()

  const c = convertToDesignProps(data?.content)
  const limit = data?.plugin?.limitItems || 11

  const didMountRef = useRef(false)
  const firstAnimSkipRef = useRef(true)
  const ref = useRef<HTMLElement>(null)
  const eventsRef = useRef<HTMLDivElement>(null)
  const tl = useRef<GSAPTimeline | null>(null)

  const [animate, setAnimate] = useState(false)
  const [filteredItems, setFilteredItems] = useState<
    | Typo3SectionSpotSanityStoryList_Item[]
    | Typo3SectionSpotSanityPressroomList_Item[]
  >([])
  const [delayedItems, setDelayedItems] = useState(filteredItems)

  const stores = {
    story: useStoreStories,
    pressroom: useStorePressroom
  }

  //stores
  const store = stores[storeType]

  const {
    facets,
    setFacets,
    setStoreItemsToShow,
    setStoreFilteredItems,
    startDate,
    endDate,
    isTopEvents,
    setIsReady,
    search,
    sortBy,
    reset,
    setSearch
  } = store()

  //animations
  useGSAPContext({
    scope: eventsRef,
    callback() {
      tl.current = gsap.timeline({
        paused: true,
        defaults: { ease: EASE.out, duration: DUR.default / 2 }
      })
      tl.current.to(eventsRef.current, { opacity: 0 })
    }
  })

  useSanityFilterSync({
    data,
    setFacets,
    setSearch,
    facets,
    search
  })

  // Filter items based on facets
  useEffect(() => {
    // Group facets by prefix
    // @ts-expect-error - TODO: fix this
    const groupedFacets = getGroupedFacets(facets, c?.facets)

    // Combine facet and date range filtering
    const filtered = c?.items?.filter(
      (
        item:
          | Typo3SectionSpotSanityStoryList_Item
          | Typo3SectionSpotSanityPressroomList_Item
      ) => {
        // Facet filtering
        const matchesFacets = getMatchedFacets(facets, groupedFacets, item)

        const searchMatch =
          search && search.trim() !== ''
            ? item?.contentTeaser?.title
                ?.toLowerCase()
                .includes(search.toLowerCase()) ||
              item?.contentTeaser?.summary
                ?.toLowerCase()
                .includes(search.toLowerCase())
            : /* item?.textDates?.some((date) =>
              date?.firstActive?.toLowerCase().includes(search.toLowerCase())
            ) */
              true

        // Item must match both facet and date range criteria
        return matchesFacets && searchMatch
      }
    )

    // Sorting
    const sorted = sorting(filtered, sortBy)

    setFilteredItems(sorted as Typo3SectionSpotSanityStoryList_Item[])
  }, [
    c?.facets,
    c?.items,
    facets,
    startDate,
    endDate,
    isTopEvents,
    search,
    sortBy
  ])

  // common for all lists
  useEffect(() => {
    if (!didMountRef.current && filteredItems?.length > 0) {
      didMountRef.current = true
      setDelayedItems(filteredItems)
      return
    }

    setStoreFilteredItems(filteredItems?.length)

    const timer = setTimeout(() => setDelayedItems(filteredItems), 500)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems])

  const handleLoadMore = () => {
    gsap.to(eventsRef.current, {
      duration: DUR.default / 2,
      opacity: 0,
      onComplete: () => {
        setItemsToShow((prev) => Math.min(prev + limit, filteredItems?.length))
        gsap.to(eventsRef.current, { duration: DUR.default / 2, opacity: 1 })
        gsap.delayedCall(0.25, () => ScrollTrigger.refresh())
      }
    })
  }
  const { saveItemsCount, getSavedItemsCount } = useListScrollRestore({
    onLoadMore: handleLoadMore
  })

  const [itemsToShow, setItemsToShow] = useState(() => {
    try {
      const saved = getSavedItemsCount()
      return saved || limit
    } catch {
      return limit
    }
  })

  useEffect(() => {
    // Adjust itemsToShow based on filteredItems length but not more than 10
    setStoreFilteredItems(filteredItems.length)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems.length])

  useEffect(() => {
    // Adjust itemsToShow based on filteredItems length but not more than 10
    setStoreItemsToShow(itemsToShow)
    saveItemsCount(itemsToShow)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsToShow])

  //handlers
  useEffect(() => {
    if (firstAnimSkipRef.current) {
      firstAnimSkipRef.current = false
      return
    }
    setAnimate(true)
    const timer = setTimeout(() => setAnimate(false), 500)
    return () => clearTimeout(timer)
  }, [filteredItems])

  useEffect(() => {
    if (animate) {
      tl.current?.play()
    } else {
      tl.current?.reverse()
    }
  }, [animate])

  //reset all filters if noFilter component is present
  useEffect(() => {
    if (data?.plugin?.hasShowFilter === false) {
      reset()
      setIsReady(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.plugin?.hasShowFilter])

  return (
    <Section
      data={data}
      className={'s-spotsanity-list-story'}
      anim="fade-in"
      ref={ref}
    >
      <SectionHeaderContent
        titleLayout={c?.titleLayout}
        as={getTitleTag(c?.titleLayout)}
      >
        {c?.title}
      </SectionHeaderContent>
      <Container className={''}>
        {c?.buttons && c?.buttons?.length > 0 && (
          <div className="gap-gutter mt-xs mb-lg flex">
            {c?.buttons?.map((button, index) => (
              <Button {...button} key={button?.label || index}>
                {button?.label}
              </Button>
            ))}
          </div>
        )}
        {data.plugin?.hasShowFilter && (
          <SpotSanityFilters data={data} store={store} />
        )}
        <div
          className={cn(
            'relative grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6',
            storeType === 'story' ? 'gap-xs' : 'gap-gutter'
          )}
          ref={eventsRef}
        >
          {delayedItems.slice(0, itemsToShow).map((item, index) => (
            <div
              key={item?.contentTeaser?.title || index}
              className={cn(
                'sm:nth-1:col-span-2 md:col-span-2 md:nth-1:col-span-6 md:nth-2:col-span-3 md:nth-3:col-span-3 md:nth-4:col-span-3 md:nth-5:col-span-3'
              )}
            >
              {storeType === 'story' ? (
                <CardBlog
                  isTransition
                  href={item.slug}
                  nextLink
                  isLarge={index === 0}
                  overline={item?.category}
                  title={item?.contentTeaser?.title}
                  description={item?.contentTeaser?.summary}
                  image={item?.images?.[0] as Typo3Image}
                />
              ) : (
                <CardPress
                  isTransition
                  href={item.slug}
                  date={item.infoPublicationDate}
                  nextLink
                  isLarge={index === 0}
                  title={item?.contentTeaser?.title}
                  subtitle={item?.contentTeaser?.summary}
                  image={item?.images?.[0] as Typo3Image}
                />
              )}
            </div>
          ))}
        </div>
        <div>
          {delayedItems && itemsToShow < filteredItems?.length && (
            <div className="mt-lg flex justify-center">
              <Button
                variant="secondary"
                onClick={handleLoadMore}
                nextLink={false}
                as="button"
                aria-label={`Load more items. Currently showing ${itemsToShow} of ${filteredItems?.length} items`}
              >
                {D['fe.plugin.filters.loadMore']}
              </Button>
            </div>
          )}
          {delayedItems && delayedItems?.length === 0 && (
            <SpotSanityError store={store} />
          )}
        </div>
      </Container>
    </Section>
  )
}

export default memo(List)
